import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { sourceSchema, type emailDraftReviewToolDef } from '@newfront-insurance/ai-api';
import { Button, cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import type { InferToolInput, ToolUIPart } from 'ai';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, Copy, ChevronDown, Loader2, SendHorizontal } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { ClipboardEvent, ReactNode } from 'react';
import { useCopyToClipboard } from 'react-use';
import type { z } from 'zod';

import { EmailDraftReplyContext, EmailRecipients } from './email-draft-preview-details';
import { MessageResponse } from '../../../ai-elements';
import { useChatOverlayContextSafe } from '../../contexts/chat-overlay-context';
import { novaGradientClass } from '../../design-system/gradients';
import {
  emailDraftPreviewContextSchema,
  getOutlookDraftState,
  type EmailDraftPreviewContext,
  type OutlookDraftAction,
} from '../../hooks/outlook-draft/state';
import { useOutlookDraft } from '../../hooks/outlook-draft/use-outlook-draft';
import { getCountBucket, getLengthBucket, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../../lib/analytics';
import { copyElementToClipboard, copySelectedContentToClipboard, writeTextToClipboard } from '../../lib/rich-clipboard';
import { isDurableNovaChatCompletionsEndpoint } from '../../lib/transport';

type EmailDraftReviewToolInput = InferToolInput<typeof emailDraftReviewToolDef>;
type EmailDraftReviewInput = Omit<Partial<EmailDraftReviewToolInput>, 'body'> & {
  body?: string;
  source?: z.infer<typeof sourceSchema>;
};
type EmailRecipient = EmailDraftReviewToolInput['toRecipients'][number];

interface EmailDraftCardProps {
  draft: EmailDraftReviewInput;
  outlook?: OutlookDraftAction;
  preview?: EmailDraftPreviewContext;
}

type EmailDraftCopyTarget = 'body' | 'subject';

const COPY_FEEDBACK_DURATION_MS = 1600;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function isEmailImportance(value: unknown): value is NonNullable<EmailDraftReviewToolInput['importance']> {
  return value === 'low' || value === 'normal' || value === 'high';
}

function normalizeEmailDraftInput(input: unknown): EmailDraftReviewInput | null {
  if (!isRecord(input)) {
    return null;
  }

  const source = sourceSchema.safeParse(input.source);
  return {
    source: source.success ? source.data : undefined,
    bccRecipients: Array.isArray(input.bccRecipients) ? (input.bccRecipients as EmailRecipient[]) : undefined,
    body: typeof input.body === 'string' ? input.body : undefined,
    ccRecipients: Array.isArray(input.ccRecipients) ? (input.ccRecipients as EmailRecipient[]) : undefined,
    importance: isEmailImportance(input.importance) ? input.importance : undefined,
    subject: typeof input.subject === 'string' ? input.subject : undefined,
    toRecipients: Array.isArray(input.toRecipients) ? (input.toRecipients as EmailRecipient[]) : undefined,
  };
}

function EmailHeaderRow({ label, children, action }: { label: string; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex w-full items-start gap-4 leading-5">
      <div className="text-steel-400 w-14 shrink-0 pt-px text-[12px] font-semibold tracking-[1.2px] uppercase">
        {label}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0 flex-1 text-[14px] font-normal break-words text-[#262D46]">{children}</div>
          {action ? (
            <div className="shrink-0" data-nova-copy-exclude="true">
              {action}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function EmailDraftCopyButton({
  ariaLabel,
  className,
  copied,
  onCopy,
  tooltip,
}: {
  ariaLabel: string;
  className?: string;
  copied: boolean;
  onCopy: () => void;
  tooltip: string;
}) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={ariaLabel}
            className={cn('text-steel-400 hover:text-steel-500 h-6 w-6 p-0', className)}
            onClick={onCopy}
            size="small"
            type="button"
            variant="transparent"
          >
            {copied ? (
              <Check aria-label="Copied" color="currentColor" size={18} />
            ) : (
              <Copy aria-label="Copy to clipboard" color="currentColor" size={18} />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{copied ? 'Copied' : tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function EmailDraftBody({ bodyText }: { bodyText: string }) {
  if (!bodyText) {
    return <span className="text-steel-400 text-[14px] leading-6">(No body)</span>;
  }

  return (
    <MessageResponse
      className={cn(
        'text-[14px] leading-6 text-[#262D46]',
        '[&_p]:my-3 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
        '[&_table]:text-[14px]',
      )}
    >
      {bodyText}
    </MessageResponse>
  );
}

function EmailDraftBodyCopyButton({ copied, onCopy }: { copied: boolean; onCopy: () => void }) {
  return (
    <button
      aria-label={copied ? 'Email body copied' : undefined}
      className={cn(
        'text-steel-400 flex h-9 w-full items-center justify-center gap-[10px] rounded-[16px] bg-[#FAFBFC] py-2',
        'text-[12px] leading-4 font-semibold transition-colors',
        'focus-visible:ring-brand-400 hover:bg-[#F3F6F8] focus-visible:ring-2 focus-visible:outline-none',
      )}
      onClick={onCopy}
      type="button"
    >
      {copied ? (
        <Check aria-hidden="true" color="currentColor" size={18} />
      ) : (
        <Copy aria-hidden="true" color="currentColor" size={18} />
      )}
      <span>{copied ? 'Copied to clipboard' : 'Copy email to clipboard'}</span>
    </button>
  );
}

export function EmailDraftCard({ draft, outlook, preview }: EmailDraftCardProps) {
  const [expanded, setExpanded] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const detailsTransition = { duration: prefersReducedMotion ? 0 : 0.2, ease: 'easeInOut' as const };
  const detailsId = useId();
  const hintId = useId();
  const created = Boolean(outlook?.state);
  const analytics = useAnalytics();
  const [, copyToClipboard] = useCopyToClipboard();
  const draftBodyContentRef = useRef<HTMLDivElement | null>(null);
  const [copiedTarget, setCopiedTarget] = useState<EmailDraftCopyTarget | null>(null);
  const bodyText = draft.body?.trim() ?? '';
  const hasCc = Boolean(draft.ccRecipients?.length);
  const hasBcc = Boolean(draft.bccRecipients?.length);
  const subject = draft.subject?.trim() || '(no subject)';
  const mailboxAddress = outlook?.state?.mailboxAddress || preview?.mailboxAddress;

  useEffect(() => {
    if (!copiedTarget) {
      return;
    }

    const timer = window.setTimeout(() => setCopiedTarget(null), COPY_FEEDBACK_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [copiedTarget]);

  const trackCopy = (copyTarget: EmailDraftCopyTarget) => {
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.EMAIL_DRAFT_COPY_BUTTON_CLICKED, {
      bodyLengthBucket: getLengthBucket(bodyText.length),
      bccRecipientCountBucket: getCountBucket(draft.bccRecipients?.length),
      ccRecipientCountBucket: getCountBucket(draft.ccRecipients?.length),
      copyTarget,
      subjectLengthBucket: getLengthBucket(subject.length),
      toRecipientCountBucket: getCountBucket(draft.toRecipients?.length),
    });
    setCopiedTarget(copyTarget);
  };

  const copyPlainText = async (text: string) => {
    try {
      await writeTextToClipboard(text);
    } catch {
      copyToClipboard(text);
    }
  };

  const handleCopyBody = async () => {
    const copiedRichContent = await copyElementToClipboard(draftBodyContentRef.current);

    if (!copiedRichContent) {
      await copyPlainText(bodyText);
    }

    trackCopy('body');
  };

  const handleCopySubject = async () => {
    await copyPlainText(subject);
    trackCopy('subject');
  };

  const handleCopyBodySelection = (event: ClipboardEvent<HTMLDivElement>) => {
    copySelectedContentToClipboard(event, draftBodyContentRef.current);
  };

  return (
    <div
      className={cn(
        "w-full max-w-[720px] rounded-[16px] p-px font-['Open_Sans',sans-serif]",
        novaGradientClass.aiBorderIcon,
      )}
      data-testid="email-draft-review-card"
    >
      <div className="overflow-hidden rounded-[15px] bg-white text-[#262D46]">
        <AnimatePresence initial={false}>
          {outlook?.state && (
            <motion.div
              key="receipt"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              transition={detailsTransition}
              className="overflow-hidden"
            >
              <div
                className="relative flex min-h-11 items-center gap-2 px-4 py-2 text-[12px]"
                data-testid="email-draft-outlook-receipt"
              >
                <button
                  type="button"
                  data-testid="email-draft-outlook-expand-toggle"
                  className={cn(
                    'absolute inset-0 cursor-pointer rounded-[15px] transition-colors hover:bg-slate-50',
                    'focus-visible:ring-brand-400 focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset',
                  )}
                  aria-controls={detailsId}
                  aria-label={expanded ? 'Collapse email draft' : 'Expand email draft'}
                  aria-expanded={expanded}
                  onClick={() => setExpanded((value) => !value)}
                />
                <Check
                  aria-hidden="true"
                  size={24}
                  className="text-wtw-ultraviolet-850 pointer-events-none relative shrink-0"
                />
                <span className="pointer-events-none relative font-semibold" role="status">
                  Draft created in Outlook
                </span>
                {outlook.state.createdAt && (
                  <time
                    className="text-steel-400 pointer-events-none relative shrink-0 whitespace-nowrap"
                    dateTime={outlook.state.createdAt}
                  >
                    {new Date(outlook.state.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </time>
                )}
                {outlook.state.webLink?.startsWith('https://') && (
                  <a
                    className="text-wtw-ultraviolet-850 relative ml-auto font-semibold whitespace-nowrap hover:underline"
                    data-testid="email-draft-outlook-web-link"
                    href={outlook.state.webLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Go to draft
                  </a>
                )}
                <ChevronDown
                  aria-hidden="true"
                  size={24}
                  className={cn(
                    'pointer-events-none relative shrink-0 transition-transform duration-200 motion-reduce:transition-none',
                    expanded && 'rotate-180',
                    !outlook.state.webLink?.startsWith('https://') && 'ml-auto',
                  )}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div id={detailsId}>
          <AnimatePresence initial={false}>
            {(!created || expanded) && (
              <motion.div
                key="details"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={detailsTransition}
                className="overflow-hidden"
              >
                {preview?.originalMessage && <EmailDraftReplyContext original={preview.originalMessage} />}
                <div className="flex flex-col gap-[10px] px-4 py-[10px]">
                  {mailboxAddress && (
                    <>
                      <EmailHeaderRow label="From">
                        {draft.source?.type === 'mailbox' ? mailboxAddress : <>You &lt;{mailboxAddress}&gt;</>}{' '}
                        <span className="font-semibold">via Outlook</span>
                      </EmailHeaderRow>
                      <div className="h-px w-full bg-[#DEE2E7]" />
                    </>
                  )}
                  <EmailHeaderRow label="To">
                    <EmailRecipients recipients={draft.toRecipients} />
                  </EmailHeaderRow>
                  {hasCc && (
                    <>
                      <div className="h-px w-full bg-[#DEE2E7]" />
                      <EmailHeaderRow label="Cc">
                        <EmailRecipients recipients={draft.ccRecipients} />
                      </EmailHeaderRow>
                    </>
                  )}
                  {hasBcc && (
                    <>
                      <div className="h-px w-full bg-[#DEE2E7]" />
                      <EmailHeaderRow label="Bcc">
                        <EmailRecipients recipients={draft.bccRecipients} />
                      </EmailHeaderRow>
                    </>
                  )}
                  <div className="h-px w-full bg-[#DEE2E7]" />
                  <EmailHeaderRow
                    action={
                      <EmailDraftCopyButton
                        ariaLabel={copiedTarget === 'subject' ? 'Email subject copied' : 'Copy email subject'}
                        className="-my-1"
                        copied={copiedTarget === 'subject'}
                        onCopy={() => void handleCopySubject()}
                        tooltip="Copy subject"
                      />
                    }
                    label="Subject"
                  >
                    <span className="font-semibold">{subject}</span>
                  </EmailHeaderRow>
                </div>
                <div className="h-px w-full bg-[#DEE2E7]" />
                <div className="flex flex-col gap-4 px-4 py-3">
                  <div onCopy={handleCopyBodySelection} ref={draftBodyContentRef}>
                    <EmailDraftBody bodyText={bodyText} />
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="min-w-[180px] flex-1">
                      <EmailDraftBodyCopyButton copied={copiedTarget === 'body'} onCopy={() => void handleCopyBody()} />
                    </div>
                    {outlook && !created && (
                      <Button
                        type="button"
                        variant="transparent"
                        data-testid="email-draft-create-outlook-button"
                        aria-disabled={outlook.pending || !outlook.canCreate}
                        aria-busy={outlook.pending}
                        aria-describedby={!outlook.canCreate && !outlook.error ? hintId : undefined}
                        className={cn(
                          'flex h-9 min-w-[180px] flex-1 items-center justify-center gap-2 rounded-full px-4 py-2 text-[14px] font-semibold sm:w-[230px] sm:flex-none',
                          novaGradientClass.aiBorderIcon,
                          'text-wtw-ultraviolet-850 focus-visible:ring-brand-400 focus-visible:ring-2 focus-visible:outline-none',
                          outlook.pending
                            ? 'cursor-wait opacity-60'
                            : !outlook.canCreate
                              ? 'cursor-not-allowed opacity-60'
                              : '',
                        )}
                        onClick={() => {
                          if (outlook.canCreate && !outlook.pending) {
                            void outlook.create();
                          }
                        }}
                      >
                        {outlook.pending ? (
                          <Loader2 aria-hidden="true" size={16} className="animate-spin" />
                        ) : (
                          <SendHorizontal aria-hidden="true" size={18} />
                        )}
                        {outlook.pending ? 'Creating draft…' : 'Create draft in Outlook'}
                      </Button>
                    )}
                  </div>
                  {outlook && !outlook.canCreate && !outlook.error && !created && (
                    <p id={hintId} className="text-steel-400 text-[12px]">
                      Available when this response finishes.
                    </p>
                  )}
                  {outlook?.error && !created && (
                    <p role="alert" data-testid="email-draft-outlook-error" className="text-[12px] text-red-600">
                      {outlook.error}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function DurableEmailDraftCard({
  chatUuid,
  messageUuid,
  part,
  draft,
  preview,
}: {
  preview?: EmailDraftPreviewContext;
  chatUuid: string;
  messageUuid: string;
  part: ToolUIPart;
  draft: EmailDraftReviewInput;
}) {
  const outlook = useOutlookDraft(chatUuid, messageUuid, part);
  return <EmailDraftCard draft={draft} outlook={outlook} preview={preview} />;
}

export function EmailDraftReviewTool({ part, messageUuid }: { part: ToolUIPart; messageUuid: string }) {
  const context = useChatOverlayContextSafe();
  const outlookDraftPreviewEnabled = useFeatureFlag('nova-outlook-draft-preview', false);
  if (!('input' in part)) {
    return null;
  }

  const draft = normalizeEmailDraftInput(part.input);
  const parsedPreview = emailDraftPreviewContextSchema.safeParse(
    'output' in part && isRecord(part.output) ? part.output.preview : undefined,
  );
  const preview = parsedPreview.success ? parsedPreview.data : undefined;
  if (!draft) {
    return null;
  }

  if (
    outlookDraftPreviewEnabled &&
    context &&
    !context.isReadOnly &&
    isDurableNovaChatCompletionsEndpoint(context.apiEndpoint) &&
    part.state === 'output-available'
  ) {
    return (
      <DurableEmailDraftCard
        key={`${context.chatUuid}/${messageUuid}/${part.toolCallId}`}
        chatUuid={context.chatUuid}
        messageUuid={messageUuid}
        part={part}
        draft={draft}
        preview={preview}
      />
    );
  }
  const receipt = getOutlookDraftState(part);
  if (receipt?.status === 'created') {
    return (
      <EmailDraftCard
        draft={draft}
        preview={preview}
        outlook={{ state: receipt, canCreate: false, pending: false, error: undefined, create: async () => undefined }}
      />
    );
  }
  return <EmailDraftCard draft={draft} preview={outlookDraftPreviewEnabled ? preview : undefined} />;
}
